import type { ReactNode } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

/**
 * A card that is also, on request, the whole printed page.
 *
 * Printing to a new browser tab loses the app's own stylesheet and is one more
 * thing that can fail to load on hotel wifi. Instead each section stays on
 * this page and is hidden from `@media print` by default; the host's own
 * "Print" button flips `printing` to true on exactly one section before
 * calling `window.print()` (see HostExportTab), so only that section reaches
 * the printer — not the app shell, not the other two sections, not this
 * card's own buttons.
 *
 * `onPrint` and `onDownloadCsv` are absent while the section has nothing to
 * show yet. A Print click during a load would have opened the dialog on an
 * empty table; a Download click would have produced a header-only file.
 */
export default function PrintableSection({
  title,
  description,
  printing,
  onPrint,
  onDownloadCsv,
  downloadLabel = 'Download CSV',
  children,
}: {
  title: string;
  description?: string;
  printing: boolean;
  /** Omitted while there is nothing yet to print. */
  onPrint?: () => void;
  /** Omitted while there is nothing yet to download. */
  onDownloadCsv?: () => void;
  downloadLabel?: string;
  children: ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 3,
        '@media print': printing
          ? { display: 'block', border: 'none', boxShadow: 'none', p: 0 }
          : { display: 'none' },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2, '@media print': { mb: 1.5 } }}
      >
        <Box>
          {/* One title, on screen and on paper. The buttons and the
              description are what stay off the page. */}
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ '@media print': { display: 'none' } }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} sx={{ '@media print': { display: 'none' } }}>
          <Button variant="outlined" onClick={onDownloadCsv} disabled={!onDownloadCsv}>
            {downloadLabel}
          </Button>
          <Button variant="contained" onClick={onPrint} disabled={!onPrint}>
            Print
          </Button>
        </Stack>
      </Stack>
      {children}
    </Paper>
  );
}
